import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, Plus, Filter, FileText, Clock, CheckCircle, XCircle, AlertTriangle, ArrowLeft, Hash, ListChecks } from 'lucide-react';
import { usePermissions } from '../../context/PermissionContext';
import { useSite } from '../../context/SiteContext';
import { formatDateTime } from '../../utils/dateUtils';
import TabNavigation from '../../components/TabNavigation';
import PTWNumberingSettings from './PTWNumberingSettings';
import PTWChecklistSettings from './PTWChecklistSettings';
import { isAdmin, isHSEManager, isSafetyManager } from '../../utils/roleUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PTWList = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  // Workers & Contractors, Numbering and Checklist config are management tools.
  // QA #113: visibility is driven by the backend's own can_manage flag rather
  // than a client-side role list — the two lists had drifted, so non-admins
  // could open a config screen that then refused to save. The role helpers are
  // the optimistic initial value; the server's answer wins once it arrives.
  const [canManagePtwConfig, setCanManagePtwConfig] = useState(
    isAdmin(user) || isHSEManager(user) || isSafetyManager(user)
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/permit-checklist-config`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) setCanManagePtwConfig(!!res.data?.can_manage);
      } catch (e) {
        if (!cancelled) setCanManagePtwConfig(false);  // fail closed
      }
    })();
    return () => { cancelled = true; };
  }, []);
  const { activeSiteId } = useSite();
  const [permits, setPermits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filters
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');

  // PTW numbering configuration modal
  const [showNumbering, setShowNumbering] = useState(false);
  const [showChecklistCfg, setShowChecklistCfg] = useState(false);

  // Pagination — mirrors the pattern in safety/IncidentIntelligence.js
  const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    fetchPermits();
  }, [statusFilter, typeFilter, activeSiteId]);

  // Reset to page 1 whenever the underlying list or page size changes.
  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter, typeFilter, activeSiteId, pageSize, permits.length]);

  const totalPages = Math.max(1, Math.ceil(permits.length / pageSize));
  const safePage = Math.min(currentPage, totalPages);
  const pageStart = (safePage - 1) * pageSize;
  const pageEnd = Math.min(pageStart + pageSize, permits.length);
  const paginatedPermits = permits.slice(pageStart, pageEnd);

  const fetchPermits = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      let url = `${API}/permits`;
      const params = new URLSearchParams();

      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (typeFilter !== 'all') params.append('permit_type', typeFilter);
      if (activeSiteId) params.append('site_id', activeSiteId);

      if (params.toString()) url += `?${params.toString()}`;

      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setPermits(response.data);
      setLoading(false);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load permits');
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'Draft': 'bg-gray-100 text-gray-800',
      'Pending Approval': 'bg-yellow-100 text-yellow-800',
      'Approved': 'bg-blue-100 text-blue-800',
      'Active': 'bg-green-100 text-green-800',
      'Suspended': 'bg-red-100 text-red-800',
      'Cancelled': 'bg-red-200 text-red-900',
      'Closed': 'bg-purple-100 text-purple-800',
      'Rejected': 'bg-red-100 text-red-800',
      'Expired': 'bg-gray-100 text-gray-800',
      'Extension Requested': 'bg-orange-100 text-orange-800',
      'Under Observation': 'bg-amber-100 text-amber-800',
      'HSE Verified': 'bg-green-100 text-green-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusIcon = (status) => {
    if (status === 'Active') return <CheckCircle className="w-4 h-4" />;
    if (status === 'Suspended' || status === 'Rejected' || status === 'Cancelled') return <XCircle className="w-4 h-4" />;
    if (status === 'Pending Approval' || status === 'Extension Requested') return <Clock className="w-4 h-4" />;
    if (status === 'Expired') return <AlertTriangle className="w-4 h-4" />;
    return <FileText className="w-4 h-4" />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <TabNavigation user={user} onLogout={onLogout} />
      <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <Button
          variant="outline"
          onClick={() => navigate('/safety')}
          className="mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Dashboard
        </Button>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Permit to Work</h1>
            <p className="text-gray-600 mt-1">Manage work permits for high-risk activities</p>
          </div>
          <div className="flex items-center gap-3">
            {canManagePtwConfig && (
              <Button
                variant="outline"
                onClick={() => setShowNumbering(true)}
                title="Configure permit numbering formats"
              >
                <Hash className="w-4 h-4 mr-2" />
                Numbering
              </Button>
            )}
            {canManagePtwConfig && (
              <Button
                variant="outline"
                onClick={() => setShowChecklistCfg(true)}
                title="Configure the safety checklist per permit type"
              >
                <ListChecks className="w-4 h-4 mr-2" />
                Checklist
              </Button>
            )}
            {hasPermission('ptw', 'create') && (
              <Button
                onClick={() => navigate('/ptw/create')}
                className="bg-blue-600 hover:bg-blue-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Permit
              </Button>
            )}
          </div>
        </div>

        <PTWNumberingSettings open={showNumbering} onClose={() => setShowNumbering(false)} />
        <PTWChecklistSettings open={showChecklistCfg} onClose={() => setShowChecklistCfg(false)} />

        {/* Filters */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-gray-500" />
                <span className="text-sm font-semibold text-gray-700">Filters:</span>
              </div>
              
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Statuses</option>
                <option value="Draft">Draft</option>
                <option value="Pending Approval">Pending Approval</option>
                <option value="Approved">Approved</option>
                <option value="Active">Active</option>
                <option value="Closed">Closed</option>
                <option value="Suspended">Suspended</option>
                <option value="Expired">Expired</option>
              </select>

              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Types</option>
                <option value="Hot Work">Hot Work</option>
                <option value="Cold Work">Cold Work</option>
                <option value="Confined Space">Confined Space</option>
                <option value="Work at Height">Work at Height</option>
                <option value="Electrical Work">Electrical Work</option>
                <option value="Excavation">Excavation</option>
                <option value="Lifting Operations">Lifting Operations</option>
                <option value="Chemical Handling">Chemical Handling</option>
                <option value="Isolation Permit">Isolation Permit</option>
                <option value="Radiography">Radiography</option>
                <option value="General Maintenance">General Maintenance</option>
              </select>

              {(statusFilter !== 'all' || typeFilter !== 'all') && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setStatusFilter('all');
                    setTypeFilter('all');
                  }}
                >
                  Clear Filters
                </Button>
              )}

              <div className="ml-auto flex items-center gap-3 text-sm">
                <span className="text-gray-600">
                  {permits.length === 0
                    ? 'No permits'
                    : `Showing ${pageStart + 1}-${pageEnd} of ${permits.length}`}
                </span>
                <label className="flex items-center gap-1 text-gray-600">
                  Page size
                  <select
                    value={pageSize}
                    onChange={(e) => setPageSize(parseInt(e.target.value, 10))}
                    className="px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {PAGE_SIZE_OPTIONS.map(n => (
                      <option key={n} value={n}>{n}</option>
                    ))}
                  </select>
                </label>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Error Alert */}
      {error && (
        <Alert variant="destructive" className="mb-6">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Permits List */}
      <div className="grid grid-cols-1 gap-4">
        {permits.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">No permits found</p>
              <Button
                onClick={() => navigate('/ptw/create')}
                className="mt-4"
              >
                Create Your First Permit
              </Button>
            </CardContent>
          </Card>
        ) : (
          paginatedPermits.map((permit) => (
            <Card
              key={permit.id}
              className="hover:shadow-lg transition-shadow cursor-pointer"
              onClick={() => navigate(`/ptw/${permit.id}`)}
            >
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-bold text-gray-900">
                        {permit.permit_number}
                      </h3>
                      <span className="px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800">
                        {permit.permit_type}
                      </span>
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1 ${getStatusColor(permit.status)}`}>
                        {getStatusIcon(permit.status)}
                        {permit.status}
                      </span>
                    </div>
                    
                    <p className="text-gray-700 mb-3">{permit.work_description}</p>
                    
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      {permit.site_name && (
                        <div>
                          <span className="text-gray-500">Site:</span>
                          <p className="font-semibold text-blue-700">{permit.site_name}</p>
                        </div>
                      )}
                      <div>
                        <span className="text-gray-500">Location:</span>
                        <p className="font-semibold text-gray-900">{permit.location}</p>
                      </div>
                      <div>
                        <span className="text-gray-500">Applicant:</span>
                        <p className="font-semibold text-gray-900">{permit.applicant_name}</p>
                      </div>
                      <div>
                        <span className="text-gray-500">Valid From:</span>
                        <p className="font-semibold text-gray-900">
                          {formatDateTime(permit.work_start_date)}
                        </p>
                      </div>
                      <div>
                        <span className="text-gray-500">Valid To:</span>
                        <p className="font-semibold text-gray-900">
                          {formatDateTime(permit.work_end_date)}
                        </p>
                      </div>
                    </div>

                    {permit.status === 'Pending Approval' && permit.approvals && permit.approvals.length > 0 && (
                      <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 bg-yellow-50 border border-yellow-200 rounded-lg">
                        <Clock className="w-4 h-4 text-yellow-600" />
                        <span className="text-sm text-yellow-800">
                          <span className="font-semibold">Pending with: </span>
                          {permit.approvals[permit.current_approval_level || 0]?.approver_name}
                          {permit.approvals[permit.current_approval_level || 0]?.approver_role &&
                            ` (${permit.approvals[permit.current_approval_level || 0].approver_role})`
                          }
                        </span>
                      </div>
                    )}

                    {permit.contractor_company && (
                      <div className="mt-3">
                        <span className="text-sm text-gray-500">Contractor: </span>
                        <span className="text-sm font-semibold text-gray-900">{permit.contractor_company}</span>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Pagination — hidden when everything fits on one page. */}
      {permits.length > pageSize && (
        <div className="bg-white rounded-xl shadow-sm p-3 mt-4 flex flex-wrap items-center justify-between gap-3 text-sm">
          <div className="text-gray-600">
            Page <span className="font-semibold">{safePage}</span> of{' '}
            <span className="font-semibold">{totalPages}</span>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setCurrentPage(1)}
              disabled={safePage === 1}
              className="px-3 py-1 rounded border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              « First
            </button>
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={safePage === 1}
              className="px-3 py-1 rounded border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              ‹ Prev
            </button>
            {(() => {
              const window_ = 2;
              let start = Math.max(1, safePage - window_);
              let end = Math.min(totalPages, start + 4);
              start = Math.max(1, end - 4);
              const pages = [];
              for (let p = start; p <= end; p++) pages.push(p);
              return pages.map(p => (
                <button
                  key={p}
                  onClick={() => setCurrentPage(p)}
                  className={`px-3 py-1 rounded border text-sm ${
                    p === safePage
                      ? 'bg-[#1B5C94] text-white border-[#1B5C94]'
                      : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {p}
                </button>
              ));
            })()}
            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={safePage === totalPages}
              className="px-3 py-1 rounded border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Next ›
            </button>
            <button
              onClick={() => setCurrentPage(totalPages)}
              disabled={safePage === totalPages}
              className="px-3 py-1 rounded border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Last »
            </button>
          </div>
        </div>
      )}
    </div>
    </div>
  );
};

export default PTWList;
